import React, { useState, useEffect, useContext } from 'react';
import { Link } from '@this/components/shared/ui/navigations/link';
import { Modal, ModalHeader, ModalBody } from '@this/components/shared/ui/feedbacks/modal';
import Box from '@material-ui/core/Box';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Loading } from '@this/components/shared/ui/feedbacks/loading';
import { Fetcher } from '@this/src/util';
import OrderItemCalendarEvent, {
  convertOrderItemCalendarEventResponseToArgs
} from '@this/src/domain/order_item_calendar_event';
import type { OrderItemCalendarEventResponseArgs } from '@this/src/domain/order_item_calendar_event';
import type OrderItem from '@this/src/domain/order_item';
import CoreAppContext from '@this/components/core_app/core_app_context';
import { createCalendarLink, formatMainContentBodyText, formatDate, isAllday } from '../trip_calendar_helpers';
import type { Theme } from '../trip_calendar_helpers'; // Themeの型を別途import
import { CalendarLinkButton } from './calendar_link_button';

interface GoogleCalendarAddButtonProps {
  trip: any;
  user: any;
  theme: Theme;
  orderItem: OrderItem;
}

const GoogleCalendarAddButton: React.FC<GoogleCalendarAddButtonProps> = ({ trip, user, theme, orderItem }) => {
  const { serviceId, env } = useContext(CoreAppContext);
  const [showGoogleCalendarAuthModal, setShowGoogleCalendarAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredCalendarEvent, setRegisteredCalendarEvent] = useState<OrderItemCalendarEvent | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 既存のカレンダーイベントを確認
  useEffect(() => {
    if (orderItem?.orderItemCalendarEvents) {
      // orderItemCalendarEventsがオブジェクトの場合は配列に変換
      const existingEvent = orderItem.orderItemCalendarEvents.find(
        event => event.connectedCalendar?.provider === 'google'
      );
      if (existingEvent) {
        setRegisteredCalendarEvent(existingEvent);
      }
    }
  }, [orderItem]);

  const handleGoogleCalendarAddClick = async () => {
    const isCalendarConnected = !!user?.google_calendar_connected;

    if (!isCalendarConnected) {
      setShowGoogleCalendarAuthModal(true);
    } else {
      // 連携済みの場合: APIを呼び出して実際にカレンダーに登録
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await Fetcher.post<OrderItemCalendarEventResponseArgs>(
          `/trips/${trip.id}/calendar_events.json`,
          {
            trace_id: orderItem?.traceId,
            calendar: 'google'
          }
        );

        setRegisteredCalendarEvent(
          new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(response))
        );
      } catch (error: any) {
        console.error('Calendar registration failed:', error);
        const errorMsg =
          error.response?.data?.error || 'カレンダーへの予定登録に失敗しました。時間をおいて再度お試しください。';
        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const hideGoogleCalendarAuthModal = () => {
    setShowGoogleCalendarAuthModal(false);
  };

  const handleGoogleCalendarAuth = () => {
    let currentUrl = window.location.href;
    const traceId = orderItem?.traceId;

    if (!currentUrl.includes(`google_calendar_connected=true`)) {
      if (currentUrl.includes('?')) {
        currentUrl += `&google_calendar_connected=true`;
      } else {
        currentUrl += `?google_calendar_connected=true`;
      }
    }

    if (traceId) {
      const traceIdParam = `trace_id=${encodeURIComponent(traceId)}`;
      if (!currentUrl.includes(traceIdParam)) {
        if (currentUrl.includes('?')) {
          currentUrl += `&${traceIdParam}`;
        } else {
          currentUrl += `?${traceIdParam}`;
        }
      }
    }

    window.location.href = `${config.authUrl}?redirect_to=${encodeURIComponent(currentUrl)}`;
  };

  // 登録済みかどうかの判定
  const isRegistered = !!registeredCalendarEvent;

  // Googleカレンダーの設定
  const config = {
    iconPath: '/images/google-icon.png',
    buttonText: 'Googleカレンダーに追加',
    authUrl: `/users/calendar_auth/google`,
    authButtonText: 'Googleカレンダー連携',
    fallbackText: '→連携せずにGoogleカレンダーのサイト上で新規作成'
  };

  const calendarLink = createCalendarLink(
    orderItem.mappingDescription(),
    formatMainContentBodyText(orderItem, trip, user.id, theme, serviceId, 'google'),
    formatDate(orderItem, 'google'),
    formatDate(orderItem, 'google', true),
    'google',
    isAllday(orderItem)
  );

  return (
    <>
      {/* Googleのアプリレビューが終わるまで、本番環境にOAuth連携の機能は出さない（既存のリンク機能だけ出す） */}
      {env === 'production' ? (
        <CalendarLinkButton href={calendarLink}>
          <img
            src="/images/google-icon.png"
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt="Google"
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            Googleカレンダーに追加
          </span>
        </CalendarLinkButton>
      ) : isRegistered ? (
        <CalendarLinkButton href={registeredCalendarEvent?.htmlLink || '#'}>
          <img
            src={config.iconPath}
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt="Google Calendar"
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            予定登録済み<span style={{ color: '#28a745', marginLeft: '4px' }}>✓</span>
          </span>
        </CalendarLinkButton>
      ) : (
        <CalendarLinkButton onClick={isLoading ? undefined : handleGoogleCalendarAddClick}>
          <img
            src={config.iconPath}
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt="Google Calendar"
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            {isLoading ? <Loading size="small" centerd={false} /> : config.buttonText}
          </span>
        </CalendarLinkButton>
      )}

      {errorMessage && (
        <div
          style={{
            color: 'red',
            fontSize: '11px',
            marginTop: '4px'
          }}
        >
          {errorMessage}
        </div>
      )}

      <Modal open={showGoogleCalendarAuthModal} onClose={hideGoogleCalendarAuthModal}>
        <ModalHeader>カレンダー連携</ModalHeader>
        <ModalBody>
          <p>カレンダーに予定を登録するには、カレンダーの連携をしてください</p>
          <Box mt={2}>
            <Button color="primary" onClick={handleGoogleCalendarAuth}>
              {config.authButtonText}
            </Button>
          </Box>
          <Box mt={4}>
            {orderItem && (
              <Link href={calendarLink} target="_blank" rel="noopener noreferrer">
                {config.fallbackText}
              </Link>
            )}
          </Box>
        </ModalBody>
      </Modal>
    </>
  );
};

export default GoogleCalendarAddButton;
